import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setAuthToken, setWorkspaceId } from '@/services/api';
import { integrationsApi } from '@/services/api/integrationApi';
import { takeShopifyHandoff } from '../lib/shopifyHandoff';
import { Loader2 } from 'lucide-react';
import { shopifyConnectedDestination } from '../lib/shopifyConnectedDestination';
import { forgetGoogleSignInRedirect, takeGoogleSignInRedirect } from '../app/loginRedirect';
import { completeGoogleSignIn, forgetGoogleSignIn } from '../lib/googleSignIn';
import api from '../services/api';

export function AuthCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // One return, one landing. StrictMode runs this effect twice in development;
  // the second run would find the remembered destination already taken and
  // send the merchant to /business over the top of it.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const error = params.get('error');

    const messages: Record<string, string> = {
      no_code: 'Google sign-in was cancelled.',
      token_failed: 'Could not verify your Google account. Please try again.',
      no_email: 'No email found in your Google account.',
      spots_full: 'All early access spots are taken. Check back soon!',
      oauth_failed: 'Something went wrong. Please try again.',
    };
    // This entry's URL carried a one-time code; landing replaces it, so Back
    // never comes here again.
    const fail = (reason: string | null): void => {
      // The destination belonged to this attempt; a retry starts again from /login.
      forgetGoogleSignInRedirect();
      forgetGoogleSignIn();
      const to = reason === null
        ? '/login'
        : `/login?error=${encodeURIComponent(messages[reason] || 'Sign-in failed')}`;
      navigate(to, { replace: true });
    };

    if (error) {
      fail(error);
      return;
    }
    if (!params.has('code') && !params.has('token')) {
      fail(null);
      return;
    }

    // Only a sign-in this tab started can finish here: the code redeems with
    // the verifier the button parked (lib/googleSignIn), and a session handed
    // over in the URL is not taken from a link this tab never asked for.
    void completeGoogleSignIn(params).then((session) => {
      if (!session) {
        fail('invalid_state');
        return;
      }
      setAuthToken(session.token);
      if (session.workspaceId) {
        setWorkspaceId(session.workspaceId);
      }
      // Where /login was sending the merchant before Google (a Shopify return,
      // a deep link — see app/loginRedirect). Taken now whatever follows, so it
      // is never followed twice; validated again as it is read.
      const destination = takeGoogleSignInRedirect();
      // A Shopify App Store install that went through "Sign in/up with
      // Google": the store was parked before the round trip (see
      // lib/shopifyHandoff); attach it now that the session exists.
      const shop = takeShopifyHandoff();
      if (shop) {
        integrationsApi.shopifyFinalize(shop)
          .then(() => shopifyConnectedDestination(() => api.settings.getBrand()))
          .then((to) => navigate(to, { replace: true }))
          .catch(() => navigate('/account?tab=integrations&shopify=error&reason=finalize_failed', { replace: true }));
        return;
      }
      navigate(destination ?? '/business', { replace: true });
    });
  }, [navigate, params]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-900 mx-auto mb-4" />
        <p className="text-gray-600">Signing you in...</p>
      </div>
    </div>
  );
}
